import { useState, useEffect } from "react";
import { useCollection } from "../firebase-files/hooks/useCollection";
import { useDocument } from "../firebase-files/hooks/useDocument";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";

const VendorLocations = () => {
  const { user } = useAuthContext();

  const { document: multiLocation } = useDocument("settings", "multiLocation");
  const { documents: vendors } = useCollection("vendors");

  const [showVendorModal, setShowVendorModal] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);

  const [locationCity, setLocationCity] = useState("");
  const [locationName, setLocationName] = useState("");

  // ⭐ Selected Location stored in LOCAL STORAGE
  const [selectedLocation, setSelectedLocation] = useState(
    localStorage.getItem("selectedLocation") || "all"
  );

  // 🔄 Sync selectedLocation across all tabs/components
  useEffect(() => {
    const syncLocation = (e) => {
      if (e.key === "selectedLocation") {
        setSelectedLocation(e.newValue);
      }
    };
    window.addEventListener("storage", syncLocation);
    return () => window.removeEventListener("storage", syncLocation);
  }, []);

  // Sync across same-tab components
  useEffect(() => {
    const updateLocation = () => {
      const newLocation = localStorage.getItem("selectedLocation") || "all";
      setSelectedLocation(newLocation);
    };

    window.addEventListener("selectedLocationChanged", updateLocation);

    return () => {
      window.removeEventListener("selectedLocationChanged", updateLocation);
    };
  }, []);

  /** -----------------------------------------------------
   * AUTO POPUP WHEN selectedLocation IS "all"
   ----------------------------------------------------- */
  useEffect(() => {
    if (!multiLocation || !vendors) return;

    const isMultiEnabled = multiLocation?.isEnabled;
    const vendorCount = vendors.length;

    if (!isMultiEnabled) return; // ❌ MULTI LOCATION DISABLED → No popup
    if (vendorCount < 1) return; // ❌ No vendor count → No popup

    if (selectedLocation === "all") {
      setShowVendorModal(true);
    } else {
      setShowVendorModal(false);
    }
  }, [selectedLocation, vendors, multiLocation]);

  /** -----------------------------------------------------
   * HANDLE LOCATION SELECT
   ----------------------------------------------------- */
  const handleVendorSelect = (vendor) => {
    if (vendor.id === selectedLocation) return;

    localStorage.setItem("selectedLocation", vendor.id);

    // update component state
    setSelectedLocation(vendor.id);

    // 🔥 IMPORTANT → notify all components in same tab
    window.dispatchEvent(new Event("selectedLocationChanged"));

    if (vendor.id !== "all") {
      setShowVendorModal(false);
    }
  };

  /** -----------------------------------------------------
   * PREPARE LIST
   ----------------------------------------------------- */
  let combinedVendors = [];

  if (multiLocation?.isAllLocation === true) {
    combinedVendors.push({
      id: "all",
      name: "All Locations",
      city: "View All",
    });
  }

  combinedVendors = [...combinedVendors, ...(vendors || [])];

  /** SELECTED LOCATION DETAILS FOR DISPLAY */
  const selectedVendorLocation =
    vendors?.find((v) => v.id === selectedLocation) || null;

  return (
    <div>
      {/* SHOW LOCATION BUTTON ONLY WHEN MULTI LOCATION ENABLED & MORE THAN 1 VENDOR */}
      {multiLocation?.isEnabled && vendors?.length > 1 && (
        <div
          className="navbar-vendor-name"
          onClick={() => setShowVendorModal(true)}
        >
          <div className="vendor-preview">
            <div className="vendor-info">
              <span className="material-symbols-outlined">location_on</span>
              <h5>
                {selectedVendorLocation
                  ? selectedVendorLocation.name
                  : "Choose Location"}
              </h5>
            </div>
          </div>
        </div>
      )}

      {/* POPUP */}
      {showVendorModal && (
        <div
          className="vendor-modal-overlay"
          onClick={() => {
            if (selectedLocation !== "all") setShowVendorModal(false);
          }}
        >
          <div className="vendor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vendor-modal-header">
              <h2>Select From Available Locations</h2>

              <span
                className="material-symbols-outlined close-button"
                onClick={() => {
                  setShowVendorModal(false);
                }}
              >
                close
              </span>
            </div>

            {/* LOCATION LIST */}
            <div className="vendor-list">
              {combinedVendors.map((vendor) => {
                const isSelected = vendor.id === selectedLocation;

                return (
                  <div
                    key={vendor.id}
                    onClick={() => handleVendorSelect(vendor)}
                    className={`vendor-card ${isSelected ? "selected" : ""}`}
                    style={{
                      opacity: isSelected ? 0.4 : 1,
                      pointerEvents: isSelected ? "none" : "auto",
                    }}
                  >
                    <div className="vendor-card-info">
                      <h3>{vendor.name}</h3>
                      <p>{vendor.city}</p>
                    </div>

                    {isSelected && (
                      <span className="material-symbols-outlined check-icon">
                        check_circle
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLocations;
